import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TutorialTargetIndicator, {
  resolveTutorialActionTarget,
} from "./TutorialTargetIndicator";

const MARGIN = 8;

function getRect(selector) {
  if (!selector) return null;
  const element = document.querySelector(selector);
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  return {
    top: Math.max(0, rect.top - MARGIN),
    left: Math.max(0, rect.left - MARGIN),
    width: Math.min(window.innerWidth, rect.width + MARGIN * 2),
    height: Math.min(window.innerHeight, rect.height + MARGIN * 2),
    element,
  };
}

function getCardPosition(rect, placement) {
  const cardWidth = Math.min(380, window.innerWidth - 24);
  const estimatedHeight = 245;
  const gap = 14;

  if (!rect) {
    return {
      left: Math.max(12, (window.innerWidth - cardWidth) / 2),
      top: Math.max(12, (window.innerHeight - estimatedHeight) / 2),
      width: cardWidth,
    };
  }

  let left = rect.left;
  let top = rect.top + rect.height + gap;

  if (placement === "top") {
    top = rect.top - estimatedHeight - gap;
  } else if (placement === "left") {
    left = rect.left - cardWidth - gap;
    top = rect.top;
  } else if (placement === "right") {
    left = rect.left + rect.width + gap;
    top = rect.top;
  }

  if (left + cardWidth > window.innerWidth - 12) {
    left = window.innerWidth - cardWidth - 12;
  }
  if (left < 12) left = 12;

  if (top + estimatedHeight > window.innerHeight - 12) {
    top = Math.max(12, rect.top - estimatedHeight - gap);
  }
  if (top < 12) {
    top = Math.min(
      window.innerHeight - estimatedHeight - 12,
      rect.top + rect.height + gap
    );
  }

  return { left, top, width: cardWidth };
}

export default function GuidedTour({
  page,
  stepIndex,
  onStepChange,
  onClose,
  onComplete,
}) {
  const { t } = useTranslation();
  const steps = page.steps || [];
  const step = steps[stepIndex];
  const [rect, setRect] = useState(null);
  const [targetMissing, setTargetMissing] = useState(false);

  useEffect(
    () => () => {
      if (page?.key !== "marketing") return;
      document
        .querySelector(
          '[data-academy="marketing-fullscreen-preview-close"]'
        )
        ?.click();
    },
    [page?.key]
  );

  useEffect(() => {
    if (!step) return undefined;

    let frame;
    let attempts = 0;

    const prepareStep = () => {
      const clickSelectors = Array.isArray(step?.prepare?.click)
        ? step.prepare.click
        : [step?.prepare?.click].filter(Boolean);

      clickSelectors.forEach((clickSelector) => {
        const clickable = document.querySelector(clickSelector);
        if (clickable && typeof clickable.click === "function") {
          clickable.click();
        }
      });

      const eventName = step?.prepare?.dispatch;
      if (eventName) {
        window.dispatchEvent(
          new CustomEvent(eventName, {
            detail: step.prepare.detail || {},
          })
        );
      }
    };

    prepareStep();

    const update = () => {
      const nextRect = getRect(step.target);
      if (nextRect) {
        setRect(nextRect);
        setTargetMissing(false);
        nextRect.element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      } else {
        attempts += 1;

        if (step?.skipIfMissing && attempts > 3) {
          if (stepIndex >= steps.length - 1) {
            onComplete();
          } else {
            onStepChange(stepIndex + 1);
          }
          return;
        }

        setTargetMissing(attempts > 8);
      }
    };

    update();

    const delayed = window.setTimeout(update, step?.prepare ? 420 : 250);
    const onLayout = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };

    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);

    return () => {
      window.clearTimeout(delayed);
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
    };
  }, [
    step,
    stepIndex,
    steps.length,
    onComplete,
    onStepChange,
  ]);

  useEffect(() => {
    if (!step?.advanceOn || !rect?.element) return undefined;

    if (step.advanceOn === "click") {
      const handler = () => {
        if (stepIndex >= steps.length - 1) onComplete();
        else onStepChange(stepIndex + 1);
      };
      rect.element.addEventListener("click", handler, { once: true });
      return () => rect.element.removeEventListener("click", handler);
    }

    return undefined;
  }, [
    step,
    rect,
    stepIndex,
    steps.length,
    onComplete,
    onStepChange,
  ]);

  const cardStyle = useMemo(
    () => getCardPosition(rect, step?.placement),
    [rect, step]
  );

  if (!step) return null;

  const last = stepIndex === steps.length - 1;

  const highlightStyle = rect
    ? {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      }
    : null;
  const actionTarget = resolveTutorialActionTarget(
    rect?.element,
    step?.advanceOn === "click" || step?.actionRequired === true,
  );

  return (
    <div className="academyTourLayer" role="dialog" aria-modal="true">
      {rect && (
        <>
          <div
            className="academyTourMask"
            style={{ top: 0, left: 0, right: 0, height: rect.top }}
          />
          <div
            className="academyTourMask"
            style={{
              top: rect.top,
              left: 0,
              width: rect.left,
              height: rect.height,
            }}
          />
          <div
            className="academyTourMask"
            style={{
              top: rect.top,
              left: rect.left + rect.width,
              right: 0,
              height: rect.height,
            }}
          />
          <div
            className="academyTourMask"
            style={{
              top: rect.top + rect.height,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
          <div
            className={`academyTourHighlight ${
              actionTarget ? "is-actionable" : ""
            }`}
            style={highlightStyle}
          />
        </>
      )}

      <TutorialTargetIndicator
        target={actionTarget}
        active={Boolean(actionTarget)}
        avoidPlacement={step?.placement}
      />

      {!rect && <div className="academyTourMask academyTourMaskFull" />}

      <article className="academyCoachCard" style={cardStyle}>
        <header>
          <div>
            <small>
              {t("academy_tour_step_progress", {
                title: t(page.title),
                current: stepIndex + 1,
                total: steps.length,
              })}
            </small>
            <h3>{t(step.title)}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label={t("close")}>
            ×
          </button>
        </header>

        <p>{t(step.body)}</p>

        {targetMissing && (
          <div className="academyTargetWarning">
            {t("academy_target_not_found")}
          </div>
        )}

        <div className="academyTourProgress">
          {steps.map((item, index) => (
            <span
              key={item.id}
              className={
                index <= stepIndex ? "is-complete" : ""
              }
            />
          ))}
        </div>

        <footer>
          <button
            type="button"
            className="academySecondaryButton"
            disabled={stepIndex === 0}
            onClick={() => onStepChange(stepIndex - 1)}
          >
            {t("back")}
          </button>

          <button
            type="button"
            className="academyPrimaryButton"
            onClick={() =>
              last ? onComplete() : onStepChange(stepIndex + 1)
            }
          >
            {last ? t("academy_finish") : t("academy_continue")}
          </button>
        </footer>
      </article>
    </div>
  );
}
